import React, { useCallback, useEffect, useState, useMemo, useContext } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import './Anchors.css';
import '../../css/utils/flex.css';
import classnames from 'classnames';
import LazyLoad from 'react-lazyload';

import AnchorStore from '../../store/AnchorStore';
import { getPageTitleByDomain } from '../../utils/SEOUtils';
import AnchorPageTitle from '../../assets/AnchorPage/nakednews-anchors-title.png';
import Adds from '../../components/Adds/Adds';
import { RootStoreContext } from '../../context';

import Anchor from './Anchor/Anchor';
import { checkAnchorsBroadcastStatus } from './services';
import Pagination from './Pagination';
import { createBrowserHistory } from 'history';


const CHECK_ONLINE_ANCHORS_DELAY = 2000;
const ANCHOR_OPTIONS = {
  CURRENT: 'CURRENT',
  GUEST: 'GUEST',
  PAST: 'PAST',
};
const ANCHORS_PER_PAGE = 16;

const anchorsToShow = ['currentAnchors', 'guestAnchors', 'pastAnchors'];


function Anchors(props) {
  const history = createBrowserHistory();
  const context = useContext(RootStoreContext);
  const { uiStore: { notificationsDerivation } } = context;
  const { data: notificationData } = notificationsDerivation;

  const { user, isGuestHomePage } = props;
  const urlParams = new URLSearchParams(window.location.search);
  const anchorOptionFromUrl = urlParams?.get?.('anchorCategory')?.toUpperCase();
  const defaultOption = ANCHOR_OPTIONS?.[anchorOptionFromUrl] || ANCHOR_OPTIONS.CURRENT;
  const pageFromUrl = urlParams?.get?.('page');
  const pageNum = Math.abs(parseInt(pageFromUrl, 10) - 1) || 0;

  const [onlineAnchorsIds, setOnlineAnchorsIds] = useState({});
  const [selectedOption, setSelectedOption] = useState(defaultOption);
  const [anchorsList, setAnchorsList] = useState({});
  const [page, setPage] = useState(pageNum);
  const [totalPages, setTotalPages] = useState(0);

  const getCategoryOption = (category) => {
    switch (category) {
      case 'currentAnchors':
        return ANCHOR_OPTIONS.CURRENT;
      case 'guestAnchors':
        return ANCHOR_OPTIONS.GUEST;
      case 'pastAnchors':
        return ANCHOR_OPTIONS.PAST;
      default:
        return ANCHOR_OPTIONS.CURRENT;
    }
  };

  const sortAnchorByLiveStatus = (anchors, onlineAnchors) => {
    const liveAnchors = [];
    const offlineAnchors = [];

    anchors.forEach((anchor) => {
      if (onlineAnchors[anchor.id]) {
        liveAnchors.push(anchor);
      } else {
        offlineAnchors.push(anchor);
      }
    });

    return liveAnchors.concat(offlineAnchors);
  };

  const anchorsListByPage = useCallback((onlineAnchors) => {
    const { anchors } = AnchorStore;
    const anchorsObject = {};
    if (!anchors?.currentAnchors) {
      return anchorsObject;
    }
    anchorsToShow.forEach((category) => {
      const anchorsArray = [];
      const sortedAnchorsArrayByLiveStatus = sortAnchorByLiveStatus(anchors[category], onlineAnchors);
      let anchorsArrayWithCutoff = [];
      sortedAnchorsArrayByLiveStatus.forEach((anchor, index) => {
        const isLastIndex = anchors[category].length - 1 === index;
        const arrayLimitReached = anchorsArrayWithCutoff.length === ANCHORS_PER_PAGE - 1;
        anchorsArrayWithCutoff.push(anchor);
        if ((isLastIndex || arrayLimitReached) && anchorsArrayWithCutoff.length) {
          anchorsArray.push(anchorsArrayWithCutoff);
          anchorsArrayWithCutoff = [];
        }
      });
      const key = getCategoryOption(category);
      anchorsObject[key] = anchorsArray;
    });

    return anchorsObject;
  }, []);

  const getAnchors = useCallback(async () => {
    const { anchors } = AnchorStore;
    if (!anchors?.currentAnchors) {
      await AnchorStore.setAnchors();
    }

    checkStatus();
  }, [checkStatus]);

  const checkAnchorFanZone = useCallback(async () => {
    const { anchorsWithFanzone } = AnchorStore;
    if (!anchorsWithFanzone?.length) {
      await AnchorStore.setAnchorsWithFanzone();
    }
  }, []);

  const checkStatus = useCallback(async () => {
    const { error, data } = await checkAnchorsBroadcastStatus();
    if (!error) {
      const onlineAnchorsMap = {};
      data.data.forEach((anchor) => onlineAnchorsMap[anchor.id] = anchor?.name);
      setOnlineAnchorsIds(onlineAnchorsMap);
      setAnchorsList(anchorsListByPage(onlineAnchorsMap));
    }
  }, [anchorsListByPage]);

  useEffect(() => {
    setTotalPages(anchorsList?.[selectedOption]?.length);
  }, [anchorsList, selectedOption]);

  useEffect(() => {
    let checkOnlineStatusDelay = null;
    if (notificationData?.length) {
      checkOnlineStatusDelay = setTimeout(checkStatus, CHECK_ONLINE_ANCHORS_DELAY);
    }

    return () => {
      if (checkOnlineStatusDelay) {
        clearTimeout(checkOnlineStatusDelay);
      }
    };
  }, [checkStatus, notificationData?.length]);

  const handleHistoryPush = useCallback((option, pageNumber) => {
    const params = {};
    params.anchorCategory = option;
    params.page = pageNumber + 1;
    history.push({
      pathname: window.location.pathname,
      search: `?${new URLSearchParams(params).toString()}`,
    });
  }, [history]);

  const anchorByCategory = useMemo(() => {
    const { anchorsWithFanzone } = AnchorStore;
    const isWhitelisted = user ? user.features.includes('ANCHOR_PORTAL') : false;
    let anchors = anchorsList?.[selectedOption]?.[page];
    if (!anchors) {
      anchors = anchorsList?.[selectedOption]?.[0];
      handleHistoryPush(selectedOption, 0);
    }

    return anchors?.map((anchor) => (
      <Anchor
        key={anchor.id}
        anchor={anchor}
        fanZones={anchorsWithFanzone}
        isAnchorsPage={true}
        isBroadcasting={onlineAnchorsIds[anchor.id] !== undefined}
        isGuestHomePage={isGuestHomePage}
        isWhitelisted={isWhitelisted}
      />
    ));
  }, [user, anchorsList, selectedOption, page, handleHistoryPush, onlineAnchorsIds, isGuestHomePage]);

  const onOptionSelected = useCallback((e) => {
    handleHistoryPush(e.target.value, 0);
    setSelectedOption(e.target.value);
    setPage(0);
  }, [handleHistoryPush]);

  const getAnchorOptions = useMemo(() => {
    const anchorOptions = Object.keys(ANCHOR_OPTIONS);

    return anchorOptions.map((option) => (
      <div
        key={option}
        className={classnames(
          'button-wrapper',
          { 'active': selectedOption === option }
        )}
      >
        <button
          className="anchor-category-selector-option"
          value={option}
          onClick={onOptionSelected}
        >
          {option}
        </button>
      </div>
    ));
  }, [onOptionSelected, selectedOption]);

  useEffect(() => {
    document.title = `Naked News Anchors | ${getPageTitleByDomain()}`;
    checkAnchorFanZone();
    checkStatus();
    getAnchors();
  }, []);

  const onPageChange = useCallback((e) => {
    const pageNumber = e.target.value;
    setPage(pageNumber - 1);
    handleHistoryPush(selectedOption, pageNumber - 1);
  }, [handleHistoryPush, selectedOption]);

  return (
    <div className="content" id="anchors-page">
      <div className="flex items-center justify-center border-wrapper">
        <div id="anchors-banner">
          <img alt="NAKEDNEWS ANCHORS" className="anchors-page-background" src={AnchorPageTitle} />
        </div>
        <div className="flex items-center justify-center container anchor-category-options mobile">
          <span className="flex flex-col items-center justify-evenly anchor-category-selector-options">
            {getAnchorOptions}
          </span>
        </div>
      </div>
      <div className="flex items-center justify-center container anchor-category-options desktop">
        <span className="flex items-center justify-evenly anchor-category-selector-options">
          {getAnchorOptions}
        </span>
      </div>
      <div className="container anchors-container">
        <div>
          <div className="row-r">
            {anchorByCategory}
          </div>
        </div>
        <Pagination page={page} totalPages={totalPages} onPageChange={onPageChange} />
      </div>
      <div className="flex items-center container">
        <div className="header-line" />
      </div>
      <div className="container">
        <LazyLoad>
          <Adds isAnchorPageAd={true} />
        </LazyLoad>
      </div>
    </div>
  );
}

export default observer(Anchors);

Anchors.propTypes = {
  anchorsToShow: PropTypes.array,
  isGuestHomePage: PropTypes.bool,
};

Anchors.defaultProps = {
  isGuestHomePage: false,
  anchorsToShow: ['current-anchors', 'guest-anchors', 'past-anchors'],
};
